import { supabase } from '@/lib/supabase/client'

export interface AdminLayoutOwner {
  email: string
  display_name: string | null
  tier: string
}

export interface AdminLayout {
  id: string
  name: string
  user_id: string
  location_id: string
  thumbnail_url: string | null
  is_public: boolean
  share_token: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  locations: { name: string; airport_code: string | null } | null
  owner: AdminLayoutOwner
}

export interface LayoutListParams {
  page?: number
  pageSize?: number
  search?: string
  visibility?: 'all' | 'public' | 'private'
  includeDeleted?: boolean
  userId?: string
}

export interface LayoutListResult {
  layouts: AdminLayout[]
  total: number
  page: number
  pageSize: number
}

export interface AdminLayoutDetail {
  layout: Record<string, unknown> & {
    id: string
    name: string
    user_id: string
    location_id: string
    thumbnail_url: string | null
    is_public: boolean
    share_token: string | null
    created_at: string
    updated_at: string
    deleted_at: string | null
    scene_data?: Record<string, unknown>
    item_cache?: Record<string, unknown>
    locations?: Record<string, unknown>
  }
  owner: {
    id: string
    email: string
    display_name: string | null
    tier: string
    logo_url: string | null
  }
}

/**
 * Envelope returned by the `export` actions on both admin-layouts and
 * admin-locations. Rows are verbatim `public.locations` / `public.layouts`
 * rows (scene_data included) — see docs/architecture/data-model.md §9.
 */
export interface ExportBundle {
  export_version: number
  kind: 'layout' | 'location'
  exported_at: string
  exported_by: { id: string; email: string }
  source: { project_ref: string; table_versions: Record<string, number> }
  owner: { id: string; email: string; display_name: string | null; tier: string }
  location: Record<string, unknown>
  layouts: Array<Record<string, unknown>>
  counts: { layouts: number; deleted_layouts_included: number }
}

async function invokeAdminLayouts<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-layouts', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

export function fetchAdminLayouts(params: LayoutListParams) {
  return invokeAdminLayouts<LayoutListResult>({ action: 'list', ...params })
}

export function fetchAdminLayoutDetail(layoutId: string) {
  return invokeAdminLayouts<AdminLayoutDetail>({ action: 'get', layoutId })
}

/** Full JSON bundle for one layout + its parent location. Audit-logged server-side. */
export function exportLayout(layoutId: string) {
  return invokeAdminLayouts<ExportBundle>({ action: 'export', layoutId })
}

export function softDeleteLayout(layoutId: string, reason: string) {
  return invokeAdminLayouts<{ success: boolean }>({ action: 'soft_delete', layoutId, reason })
}

export function restoreLayout(layoutId: string, reason: string) {
  return invokeAdminLayouts<{ success: boolean }>({ action: 'restore', layoutId, reason })
}

export function forceUnpublishLayout(layoutId: string, reason: string) {
  return invokeAdminLayouts<{ success: boolean }>({ action: 'force_unpublish', layoutId, reason })
}
